import React, { useEffect, useState } from "react";
import { backend_api } from "../../../../../static/server";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Typography,
  Switch,
  Tooltip,
  IconButton,
  TextField,
  Menu,
  MenuItem,
  Stack,
  Divider,
  FormControlLabel,
  Button,
} from "@mui/material";
import ColorLensRoundedIcon from "@mui/icons-material/ColorLensRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import { fieldTypeLabelMap } from "../../../../../static/staticdata";

const DefaultColors = [
  "#F87171",
  "#60A5FA",
  "#34D399",
  "#FBBF24",
  "#A78BFA",
  "#F97316",
];

const EditProperties = ({
  sectionId,
  fieldId,
  field,
  handleUpdateFieldLabel,
  handleUpdateFieldProperty,
  onClose,
  formId = "field-properties-form",
}) => {
  const hasLinkOption =
    field.pickList?.link_option &&
    (typeof field.pickList.link_option === "string" ||
      field.pickList.link_option.tab);

  const linkOptionValue = hasLinkOption
    ? typeof field.pickList.link_option === "string"
      ? field.pickList.link_option
      : field.pickList.link_option.tab
    : "";

  const defaultOptions = [
    { label: "Option 1", value: "option_1" },
    { label: "Option 2", value: "option_2" },
  ];

  const initialOptions =
    field.pickList?.options || (!hasLinkOption ? defaultOptions : []);

  const [pickListOptionCount, setPickListOptionCount] = useState(
    initialOptions.length || 2
  );
  const [colorMenuAnchor, setColorMenuAnchor] = useState(null);
  const [activeOptionIndex, setActiveOptionIndex] = useState(null);
  const [optionColors, setOptionColors] = useState(
    initialOptions.map(
      (opt, i) => opt.color || DefaultColors[i % DefaultColors.length]
    )
  );
  const [customColor, setCustomColor] = useState("#34D399");
  const [options, setOptions] = useState(initialOptions);

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      label: field.label ?? "",
      placeholder: field.placeholder ?? "",
      required: field.required ?? false,
      tooltip: field.tooltip ?? false,
      tooltipText: field.tooltipText ?? "",
      pickList: {
        link_option: linkOptionValue,
        options: initialOptions,
        multiSelect: field.pickList?.multiSelect ?? false,
        useColors: field.pickList?.useColors ?? false,
        defaultValue:
          field.pickList?.defaultValue ??
          (initialOptions.length > 0 ? initialOptions[0]?.value : "none"),
      },
      characterLimit: field.characterLimit ?? 255,
      phoneNoCharacterLimit: field.phoneNoCharacterLimit ?? 30,
    },
  });

  useEffect(() => {
    if (
      field.value === "pick_list" &&
      field.pickList &&
      !field.pickList.defaultValue &&
      initialOptions.length > 0
    ) {
      setValue("pickList.defaultValue", initialOptions[0].value);
    }
  }, [field.value, field.pickList, initialOptions, setValue]);

  const showTooltip = watch("tooltip");
  const link_option = watch("pickList.link_option");
  const useColors = watch("pickList.useColors");
  const pickListValues = watch("pickList.options") || [];

  const openColorMenu = (event, index) => {
    setActiveOptionIndex(index);
    setColorMenuAnchor(event.currentTarget);
    setCustomColor(optionColors[index] || "#34D399");
  };

  const handleColorChange = (color) => {
    if (activeOptionIndex === null) return;
    const newOptionColors = [...optionColors];
    newOptionColors[activeOptionIndex] = color;
    setOptionColors(newOptionColors);
    setValue(`pickList.options[${activeOptionIndex}].color`, color);
    setColorMenuAnchor(null);
  };

  const handleCustomColorChange = (event) => {
    setCustomColor(event.target.value);
  };

  const handleRemoveOption = (indexToRemove) => {
    if (pickListOptionCount <= 2) return;
    const currentOptions = watch("pickList.options") || [];
    const newOptions = currentOptions.filter(
      (_, index) => index !== indexToRemove
    );
    setValue("pickList.options", newOptions);
    setOptions(newOptions);
    const newColors = optionColors.filter(
      (_, index) => index !== indexToRemove
    );
    setOptionColors(newColors);
    setPickListOptionCount(pickListOptionCount - 1);

    const currentDefault = watch("pickList.defaultValue");
    const removedOptionValue = currentOptions[indexToRemove]?.value;
    if (currentDefault === removedOptionValue && newOptions.length > 0) {
      setValue("pickList.defaultValue", newOptions[0].value);
    }
  };

  useEffect(() => {
    if (link_option && link_option !== "") return;

    const currentOptions = watch("pickList.options") || [];
    if (currentOptions.length < pickListOptionCount) {
      const newOptions = [...currentOptions];
      for (let i = currentOptions.length; i < pickListOptionCount; i++) {
        newOptions.push({
          label: `Option ${i + 1}`,
          value: `option_${i + 1}`,
          color: DefaultColors[i % DefaultColors.length],
        });
      }
      setValue("pickList.options", newOptions);
      setOptions(newOptions);
    } else if (currentOptions.length > pickListOptionCount) {
      const newOptions = currentOptions.slice(0, pickListOptionCount);
      setValue("pickList.options", newOptions);
      setOptions(newOptions);
    }
  }, [pickListOptionCount, setValue, watch, link_option]);

  useEffect(() => {
    if (link_option && link_option !== "") {
      setValue("pickList.options", []);
      setOptions([]);
    } else if (
      (options.length === 0 || pickListValues.length === 0) &&
      pickListOptionCount > 0
    ) {
      const defaultOpts = [
        { label: "Option 1", value: "option_1", color: DefaultColors[0] },
        { label: "Option 2", value: "option_2", color: DefaultColors[1] },
      ];
      setValue("pickList.options", defaultOpts);
      setOptions(defaultOpts);
      setPickListOptionCount(2);
    }
  }, [
    link_option,
    setValue,
    options.length,
    pickListValues.length,
    pickListOptionCount,
  ]);

  const onSubmit = (data) => {
    const updatedPickList = { ...data.pickList };

    if (updatedPickList.link_option && updatedPickList.link_option !== "") {
      updatedPickList.link_option = {
        tab: updatedPickList.link_option,
        tab_api:
          updatedPickList.link_option === "contact"
            ? `${backend_api}contact/retrieve/`
            : updatedPickList.link_option === "matter"
            ? `${backend_api}case/register/`
            : updatedPickList.link_option === "member"
            ? `${backend_api}team/create-employee/?is_active=True`
            : null,
      };
      delete updatedPickList.options;
    } else if (updatedPickList.options) {
      updatedPickList.options = updatedPickList.options.map(
        (option, index) => ({
          ...option,
          color:
            optionColors[index] || DefaultColors[index % DefaultColors.length],
          value: option.value || `option_${index + 1}`,
          label: option.label || option.value || `Option ${index + 1}`,
        })
      );

      if (!updatedPickList.defaultValue && updatedPickList.options.length > 0) {
        updatedPickList.defaultValue = updatedPickList.options[0].value;
      }

      delete updatedPickList.link_option;
    }

    if (data.label) {
      handleUpdateFieldLabel(sectionId, fieldId, data.label);
    }

    handleUpdateFieldProperty(sectionId, fieldId, "pickList", updatedPickList);

    Object.entries(data).forEach(([key, value]) => {
      if (key !== "label" && key !== "pickList") {
        handleUpdateFieldProperty(sectionId, fieldId, key, value);
      }
    });

    onClose?.();
  };

  const fieldTypeLabel =
    fieldTypeLabelMap[field?.value] || "Unknown Field Type";

  return (
    <>
      <Box
        component="form"
        id={formId}
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          p: "1rem",
        }}
      >
        {/* Field Basics Section */}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
            Field basics
          </Typography>
          <Stack spacing={2}>
            <Controller
              name="label"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Field label"
                  placeholder="Enter field label"
                />
              )}
            />
            <Controller
              name="placeholder"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Placeholder"
                  placeholder="Enter placeholder text"
                />
              )}
            />
            <TextField
              value={fieldTypeLabel}
              label="Field type"
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              placeholder={fieldTypeLabel}
            />
          </Stack>
        </Box>

        <Divider />

        {/* Behaviour Section */}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
            Behaviour
          </Typography>
          <Stack spacing={2}>
            <Stack direction="column" spacing={1}>
              <FormControlLabel
                control={
                  <Controller
                    name="required"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Switch
                        checked={value}
                        onChange={onChange}
                        color="primary"
                      />
                    )}
                  />
                }
                label="Required"
              />
              <FormControlLabel
                control={
                  <Controller
                    name="tooltip"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Switch
                        checked={value}
                        onChange={onChange}
                        color="primary"
                      />
                    )}
                  />
                }
                label="Show tooltip"
              />
            </Stack>
            {showTooltip && (
              <Controller
                name="tooltipText"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    minRows={3}
                    label="Tooltip text"
                    placeholder="Share additional guidance for this field"
                  />
                )}
              />
            )}
          </Stack>
        </Box>

        {/* Picklist Configuration Section */}
        {field.value === "pick_list" && (
          <>
            <Divider />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                Picklist configuration
              </Typography>
              <Stack spacing={2}>
                <Stack direction="column" spacing={1}>
                  <FormControlLabel
                    control={
                      <Controller
                        name="pickList.multiSelect"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <Switch
                            checked={value}
                            onChange={onChange}
                            color="primary"
                          />
                        )}
                      />
                    }
                    label="Allow multiple selections"
                  />
                  <FormControlLabel
                    control={
                      <Controller
                        name="pickList.useColors"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <Switch
                            checked={value}
                            onChange={onChange}
                            color="primary"
                          />
                        )}
                      />
                    }
                    label="Show colors for options"
                  />
                </Stack>

                <Controller
                  name="pickList.defaultValue"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      size="small"
                      label="Default value"
                      placeholder="Choose a default option"
                      helperText="Choose the option that will be preselected"
                    >
                      <MenuItem value="none">None</MenuItem>
                      {(!link_option || link_option === "") &&
                        (pickListValues.length > 0
                          ? pickListValues
                          : options
                        ).map((option, index) => (
                          <MenuItem key={index} value={option.value}>
                            {option.value || option.label}
                          </MenuItem>
                        ))}
                    </TextField>
                  )}
                />

                {(!link_option || link_option === "") && (
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      p: 2,
                      bgcolor: "background.default",
                    }}
                  >
                    <Stack spacing={2}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          Options
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddRounded />}
                          onClick={() =>
                            setPickListOptionCount(pickListOptionCount + 1)
                          }
                        >
                          Add
                        </Button>
                      </Stack>

                      {(pickListValues.length > 0
                        ? pickListValues
                        : options
                      ).map((_, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            gap: "0.5rem",
                            alignItems: "center",
                          }}
                        >
                          {useColors && (
                            <Tooltip title="Choose color">
                              <Box
                                onClick={(event) => openColorMenu(event, index)}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 1,
                                  cursor: "pointer",
                                  border: "1px solid",
                                  borderColor: "divider",
                                  bgcolor:
                                    optionColors[index] ||
                                    DefaultColors[index % DefaultColors.length],
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <ColorLensRoundedIcon
                                  sx={{ color: "#fff", fontSize: 16 }}
                                />
                              </Box>
                            </Tooltip>
                          )}

                          <Controller
                            name={`pickList.options[${index}].value`}
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                fullWidth
                                size="small"
                                label={`Option ${index + 1}`}
                                placeholder={`Option ${index + 1}`}
                                // helperText="Visible text shown in the form"
                              />
                            )}
                          />

                          <Controller
                            name={`pickList.options[${index}].color`}
                            control={control}
                            defaultValue={
                              optionColors[index] ||
                              DefaultColors[index % DefaultColors.length]
                            }
                            render={({ field }) => (
                              <input type="hidden" {...field} />
                            )}
                          />

                          <Tooltip title="Remove option">
                            <span>
                              <IconButton
                                color="error"
                                size="small"
                                disabled={pickListOptionCount <= 2}
                                onClick={() => handleRemoveOption(index)}
                                sx={{ flexShrink: 0 }}
                              >
                                <DeleteOutlineRounded fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Box>
          </>
        )}

        {/* Character Limit Section */}
        {field.value === "single_line" && (
          <>
            <Divider />
            <Controller
              name="characterLimit"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  size="small"
                  fullWidth
                  label="Character limit"
                  placeholder="Enter the maximum characters"
                  helperText="Maximum characters allowed for this field"
                />
              )}
            />
          </>
        )}

        {/* Phone Character Limit Section */}
        {field.value === "phone" && (
          <>
            <Divider />
            <Controller
              name="phoneNoCharacterLimit"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  size="small"
                  fullWidth
                  label="Phone number character limit"
                  placeholder="Enter the maximum digits"
                  helperText="Maximum characters allowed for phone numbers"
                />
              )}
            />
          </>
        )}
      </Box>

      {/* Color Menu */}
      <Menu
        anchorEl={colorMenuAnchor}
        open={Boolean(colorMenuAnchor)}
        onClose={() => setColorMenuAnchor(null)}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
      >
        <Box sx={{ p: 2, width: 220 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Select a color
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
            {DefaultColors.map((color) => (
              <Box
                key={color}
                onClick={() => handleColorChange(color)}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  bgcolor: color,
                  cursor: "pointer",
                  border:
                    customColor === color
                      ? "2px solid rgba(0,0,0,0.2)"
                      : "1px solid rgba(0,0,0,0.1)",
                }}
              />
            ))}
          </Stack>
          <TextField
            value={customColor}
            onChange={handleCustomColorChange}
            label="Custom hex"
            size="small"
            fullWidth
            placeholder="#34D399"
            sx={{ mb: 1 }}
          />
          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={() => handleColorChange(customColor)}
          >
            Apply
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default EditProperties;
